/**
 * ChunkedUploader - Component for large file uploads with progress tracking
 * Supports files up to 100GB with resumable uploads
 */
import React, { useRef, useEffect } from 'react';
import { useChunkedUpload, UPLOAD_STATUS, formatFileSize } from '../hooks/useChunkedUpload';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
    Upload, 
    Pause, 
    Play, 
    X, 
    CheckCircle, 
    AlertCircle,
    Clock,
    RefreshCw,
    FileIcon
} from 'lucide-react';

/**
 * Status badge component
 */
function StatusBadge({ status }) {
    const statusConfig = {
        [UPLOAD_STATUS.QUEUED]: { 
            label: 'Queued', 
            color: 'bg-slate-100 text-slate-600',
            icon: Clock
        },
        [UPLOAD_STATUS.UPLOADING]: { 
            label: 'Uploading', 
            color: 'bg-blue-100 text-blue-600',
            icon: Upload
        },
        [UPLOAD_STATUS.PAUSED]: { 
            label: 'Paused', 
            color: 'bg-yellow-100 text-yellow-600',
            icon: Pause
        },
        [UPLOAD_STATUS.RESUMING]: { 
            label: 'Resuming', 
            color: 'bg-blue-100 text-blue-600',
            icon: RefreshCw
        },
        [UPLOAD_STATUS.COMPLETING]: { 
            label: 'Finalizing', 
            color: 'bg-purple-100 text-purple-600',
            icon: RefreshCw
        },
        [UPLOAD_STATUS.COMPLETED]: { 
            label: 'Complete', 
            color: 'bg-green-100 text-green-600',
            icon: CheckCircle
        },
        [UPLOAD_STATUS.FAILED]: { 
            label: 'Failed', 
            color: 'bg-red-100 text-red-600',
            icon: AlertCircle
        },
        [UPLOAD_STATUS.ABORTED]: { 
            label: 'Cancelled', 
            color: 'bg-slate-100 text-slate-400',
            icon: X
        }
    };
    
    const config = statusConfig[status] || statusConfig[UPLOAD_STATUS.QUEUED];
    const Icon = config.icon;
    
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <Icon className="h-3 w-3" />
            {config.label}
        </span>
    );
}

/**
 * Single upload item component
 */
function UploadItem({ upload, onPause, onResume, onCancel, onRemove }) {
    const isActive = [
        UPLOAD_STATUS.UPLOADING, 
        UPLOAD_STATUS.RESUMING, 
        UPLOAD_STATUS.COMPLETING
    ].includes(upload.status);
    
    const canPause = isActive && upload.status !== UPLOAD_STATUS.COMPLETING;
    const canResume = upload.status === UPLOAD_STATUS.PAUSED && !upload.needsFileReselect;
    const canRetryFinalize = upload.status === UPLOAD_STATUS.FAILED && upload.retryableFinalize && !!upload.sessionId;
    const canCancel = [UPLOAD_STATUS.QUEUED, UPLOAD_STATUS.UPLOADING, UPLOAD_STATUS.PAUSED].includes(upload.status);
    const canRemove = [UPLOAD_STATUS.COMPLETED, UPLOAD_STATUS.FAILED, UPLOAD_STATUS.ABORTED].includes(upload.status);
    
    // Format speed for display
    const formatSpeed = (bytesPerSecond) => {
        if (!bytesPerSecond || bytesPerSecond === 0) return '';
        return `${formatFileSize(bytesPerSecond)}/s`;
    };
    
    return (
        <div className="border rounded-lg p-4 bg-white">
            <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <FileIcon className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 truncate" title={upload.filename}>
                            {upload.filename}
                        </p>
                        <p className="text-xs text-slate-500">
                            {formatFileSize(upload.fileSize)}
                            {upload.totalChunks > 1 && ` • ${upload.totalChunks} chunks`}
                        </p>
                    </div>
                </div>
                <StatusBadge status={upload.status} />
            </div>
            
            {/* Progress bar */}
            <div className="mb-2">
                <Progress value={upload.progress} className="h-2" />
            </div>
            
            {/* Progress details */}
            <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                <span>
                    {formatFileSize(upload.uploadedBytes)} / {formatFileSize(upload.fileSize)}
                    {upload.progress > 0 && ` (${upload.progress.toFixed(1)}%)`}
                </span>
                <span className="flex items-center gap-2">
                    {isActive && upload.speed > 0 && (
                        <span>{formatSpeed(upload.speed)}</span>
                    )}
                    {isActive && upload.eta && (
                        <span>ETA: {upload.eta}</span>
                    )}
                    {upload.uploadedChunks > 0 && upload.totalChunks > 1 && (
                        <span>Chunks: {upload.uploadedChunks}/{upload.totalChunks}</span>
                    )}
                </span>
            </div>

            {/* Inline checksum status while uploading; final values appear in
                the file list row once the upload completes. Hidden when the
                hash is already ready and the upload has finished. */}
            {(upload.md5Status === 'calculating' || upload.md5Status === 'failed') && (
                <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-2" data-testid="upload-md5-status">
                    {upload.md5Status === 'calculating' ? (
                        <>
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
                            Calculating MD5
                            {typeof upload.md5Progress === 'number' && ` (${Math.round(upload.md5Progress * 100)}%)`}
                            …
                        </>
                    ) : (
                        <span className="text-rose-500">MD5 calculation failed (file still uploaded)</span>
                    )}
                </div>
            )}
            
            {/* Error message */}
            {upload.error && (
                <p className="text-xs text-red-600 mb-3 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {upload.error}
                </p>
            )}
            
            {/* Needs file reselect message */}
            {upload.needsFileReselect && upload.status === UPLOAD_STATUS.PAUSED && (
                <p className="text-xs text-amber-600 mb-3 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Browser session lost. Please re-upload this file to resume.
                </p>
            )}
            
            {/* Needs full restart (expired/invalid multipart session) */}
            {upload.needsRestart && (
                <p className="text-xs text-amber-600 mb-3 flex items-center gap-1" data-testid="upload-needs-restart">
                    <AlertCircle className="h-3 w-3" />
                    This upload session can no longer be resumed safely. Please re-select this file to start a fresh upload.
                </p>
            )}
            
            {/* Action buttons */}
            <div className="flex items-center gap-2">
                {canRetryFinalize && (
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onResume(upload.id)}
                        className="h-7 text-xs"
                        data-testid="upload-retry-finalize-btn"
                    >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Retry
                    </Button>
                )}
                {canPause && (
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onPause(upload.id)}
                        className="h-7 text-xs"
                    >
                        <Pause className="h-3 w-3 mr-1" />
                        Pause
                    </Button>
                )}
                {canResume && (
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onResume(upload.id)}
                        className="h-7 text-xs"
                    >
                        <Play className="h-3 w-3 mr-1" />
                        Resume
                    </Button>
                )}
                {canCancel && (
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onCancel(upload.id)}
                        className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                        <X className="h-3 w-3 mr-1" />
                        Cancel
                    </Button>
                )}
                {canRemove && (
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onRemove(upload.id)}
                        className="h-7 text-xs"
                    >
                        <X className="h-3 w-3 mr-1" />
                        Remove
                    </Button>
                )}
            </div>
        </div>
    );
}

/**
 * Main ChunkedUploader component
 */
export function ChunkedUploader({ 
    orderId, 
    category, 
    onComplete,
    onMD5AutoRetryComplete,
    onActiveChange,
    accept = "*",
    label = "Upload file",
    description = "Large files supported (up to 100GB)",
    disabled = false
}) {
    const fileInputRef = useRef(null);
    const {
        uploads,
        startUpload,
        pauseUpload,
        resumeUpload,
        cancelUpload,
        removeUpload,
        recoverFinalizingSessions,
        isUploading
    } = useChunkedUpload(orderId, category, onComplete, onMD5AutoRetryComplete);
    
    // On mount / order change, re-surface and poll any upload that was still
    // finalizing when the page was closed or refreshed (backend-owned, so it
    // completes without the original browser session and without duplicating).
    useEffect(() => {
        recoverFinalizingSessions();
    }, [recoverFinalizingSessions]);

    // Report active-upload state to the parent (used to keep the enclosing
    // Master Files section + Disc accordion expanded while work is in flight).
    // Ref-backed so an inline parent callback doesn't retrigger the effect —
    // it fires only when the boolean actually flips.
    const onActiveChangeRef = useRef(onActiveChange);
    onActiveChangeRef.current = onActiveChange;
    const hasActiveUpload = uploads.some((u) => [
        UPLOAD_STATUS.QUEUED,
        UPLOAD_STATUS.UPLOADING,
        UPLOAD_STATUS.RESUMING,
        UPLOAD_STATUS.COMPLETING,
    ].includes(u.status));
    useEffect(() => {
        if (typeof onActiveChangeRef.current === 'function') {
            onActiveChangeRef.current(hasActiveUpload);
        }
    }, [hasActiveUpload]);
    
    const handleFileSelect = (event) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        
        // Start upload for each file
        for (const file of files) {
            startUpload(file);
        }
        
        // Reset input
        event.target.value = '';
    };
    
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const files = event.dataTransfer.files;
        if (!files || files.length === 0) return;
        
        for (const file of files) {
            startUpload(file);
        }
    };
    
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    
    return (
        <div className="space-y-4">
            {/* Drop zone */}
            <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`
                    border-2 border-dashed rounded-lg p-6 text-center transition-colors
                    ${disabled ? 'border-slate-200 bg-slate-50 cursor-not-allowed' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer'}
                `}
                onClick={() => !disabled && fileInputRef.current?.click()}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept={accept}
                    onChange={handleFileSelect}
                    disabled={disabled}
                    multiple
                />
                <Upload className={`h-8 w-8 mx-auto mb-2 ${disabled ? 'text-slate-300' : 'text-slate-400'}`} />
                <p className={`text-sm font-medium ${disabled ? 'text-slate-400' : 'text-slate-700'}`}>
                    {label}
                </p>
                <p className={`text-xs mt-1 ${disabled ? 'text-slate-300' : 'text-slate-500'}`}>
                    {description}
                </p>
                <p className={`text-xs mt-1 ${disabled ? 'text-slate-300' : 'text-slate-400'}`}>
                    Click or drag and drop
                </p>
            </div>
            
            {/* Upload list */}
            {uploads.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-sm font-medium text-slate-700">
                        Uploads ({uploads.filter(u => u.status === UPLOAD_STATUS.COMPLETED).length}/{uploads.length} complete)
                    </h4>
                    {uploads.map(upload => (
                        <UploadItem
                            key={upload.id}
                            upload={upload}
                            onPause={pauseUpload}
                            onResume={resumeUpload}
                            onCancel={cancelUpload}
                            onRemove={removeUpload}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default ChunkedUploader;
